import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Label } from '../types/schema';
import { useAuthContext } from './auth-context';
import { useWorkspace } from './workspace-context';
import { labelService } from '../services/label-service';

interface LabelsContextType {
  labels: Label[];
  loading: boolean;
  error: string | null;
  refetchLabels: () => Promise<void>;
  addLabel: (label: Label) => void;
  updateLabel: (labelId: number, updates: Partial<Label>) => void;
  removeLabel: (labelId: number) => void;
}

const LabelsContext = createContext<LabelsContextType | null>(null);

/**
 * Labels Provider
 * Provides a shared cache of workspace labels to avoid duplicate API calls
 * Automatically refetches when workspace changes
 */
export function LabelsProvider({ children }: { children: ReactNode }) {
  const { user: currentUser, loading: authLoading } = useAuthContext();
  const { currentWorkspace } = useWorkspace();
  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLabels = async () => {
    if (!currentUser || !currentWorkspace) {
      setLabels([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await labelService.getWorkspaceLabels(currentWorkspace.id);
      setLabels(data);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch labels';
      console.error('Error fetching labels:', errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && currentUser && currentWorkspace) {
      fetchLabels();
    } else if (!authLoading && (!currentUser || !currentWorkspace)) {
      setLabels([]);
      setLoading(false);
    }
  }, [currentUser, currentWorkspace, authLoading]);

  // Helper functions for optimistic updates
  const addLabel = useCallback((label: Label) => {
    setLabels((prev) => [...prev, label]);
  }, []);

  const updateLabel = useCallback((labelId: number, updates: Partial<Label>) => {
    setLabels((prev) =>
      prev.map((label) =>
        label.id === labelId ? { ...label, ...updates } : label
      )
    );
  }, []);

  const removeLabel = useCallback((labelId: number) => {
    setLabels((prev) => prev.filter((label) => label.id !== labelId));
  }, []);

  return (
    <LabelsContext.Provider
      value={{
        labels,
        loading,
        error,
        refetchLabels: fetchLabels,
        addLabel,
        updateLabel,
        removeLabel,
      }}
    >
      {children}
    </LabelsContext.Provider>
  );
}

export function useLabels() {
  const context = useContext(LabelsContext);
  if (!context) {
    throw new Error('useLabels must be used within LabelsProvider');
  }
  return context;
}
